/**
 * État global de l'interface.
 *
 * Un magasin unique plutôt qu'un état éparpillé dans les composants : la session de
 * transfert est une machine à états, et cette machine doit être lisible d'un seul endroit.
 * Chaque transition est provoquée par la session (`session.ts`), jamais par un composant.
 *
 * ## Les messages sont stockés non formatés
 *
 * `error` et `warning` contiennent un `MessageDescriptor`, pas une chaîne. C'est ce qui
 * permet à une erreur affichée en français de basculer en anglais quand l'utilisateur
 * change de langue, au lieu de rester figée dans la langue où elle a été produite.
 */

import type { FileMeta, Role } from '@attrape/shared';
import { create } from 'zustand';
import type { MessageDescriptor, MessageKey } from './i18n/index.js';
import type { StrategyDecision } from './core/browser.js';
import type { ConnectionInfo } from './core/peer.js';
import type { SignalingStatus } from './core/signaling.js';
import type { ReceiverResult } from './core/transfer/receiver.js';

/** Les états par lesquels passe une session de partage. */
export type Phase =
  /** Rien en cours : l'utilisateur choisit des fichiers. */
  | 'idle'
  /** Connexion au serveur de signaling. */
  | 'connecting'
  /** Dans la salle, en attente du correspondant. */
  | 'waiting-peer'
  /** Le correspondant est là, négociation WebRTC en cours. */
  | 'negotiating'
  /** Récepteur : demande d'acceptation affichée, en attente de la décision. */
  | 'awaiting-decision'
  /** Transfert en cours. */
  | 'transferring'
  /**
   * Le lien est tombé pendant le transfert ; on tente de le rétablir.
   *
   * Distinct d'une erreur : l'utilisateur doit comprendre que le travail déjà fait est
   * conservé et qu'on réessaie, plutôt que de croire le transfert perdu. C'est la
   * différence entre « ça a échoué » et « ça reprend tout seul ».
   */
  | 'reconnecting'
  /** Transfert terminé avec succès. */
  | 'done'
  /** Échec, avec un message exploitable dans `error`. */
  | 'error';

export interface PeerSummary {
  id: string;
  role: Role;
  name?: string;
}

export interface ProgressState {
  fileName: string;
  fileIndex: number;
  fileCount: number;
  /** Octets traités pour le fichier courant. */
  fileBytes: number;
  fileTotal: number;
  /** Octets traités pour l'ensemble du lot. */
  totalBytes: number;
  totalTotal: number;
  bytesPerSecond: number;
  etaSeconds: number | null;
}

/** Demande d'acceptation présentée au récepteur. */
export interface IncomingOffer {
  files: FileMeta[];
  totalBytes: number;
  decision: StrategyDecision;
}

export interface AppState {
  phase: Phase;
  error: MessageDescriptor | null;
  warning: MessageDescriptor | null;

  signalingStatus: SignalingStatus;
  latencyMs: number | null;

  peer: PeerSummary | null;
  connection: ConnectionInfo | null;

  role: Role | null;
  shareUrl: string | null;

  incoming: IncomingOffer | null;
  progress: ProgressState | null;
  result: ReceiverResult | null;

  // ── Actions ───────────────────────────────────────────────────────────────
  setPhase: (phase: Phase) => void;
  setError: (message: MessageDescriptor) => void;
  setWarning: (message: MessageDescriptor | null) => void;
  setSignalingStatus: (status: SignalingStatus) => void;
  setLatency: (milliseconds: number | null) => void;
  setPeer: (peer: PeerSummary | null) => void;
  setConnection: (connection: ConnectionInfo | null) => void;
  setRole: (role: Role | null) => void;
  setShareUrl: (url: string | null) => void;
  setIncoming: (offer: IncomingOffer | null) => void;
  setProgress: (progress: ProgressState | null) => void;
  setResult: (result: ReceiverResult | null) => void;
  reset: () => void;
}

const INITIAL = {
  phase: 'idle' as Phase,
  error: null,
  warning: null,
  signalingStatus: 'idle' as SignalingStatus,
  latencyMs: null,
  peer: null,
  connection: null,
  role: null,
  shareUrl: null,
  incoming: null,
  progress: null,
  result: null,
};

export const useAppStore = create<AppState>((set) => ({
  ...INITIAL,

  setPhase: (phase) =>
    set((state) => ({
      phase,
      // L'avertissement affiché en cours de transfert (« gardez cet onglet au premier plan »,
      // « les données passent par le relais ») ne vaut que pendant le transfert. Le laisser
      // après la fin demande de garder ouvert un onglet dont le travail est terminé, et le
      // laisser après un échec affiche un conseil qui ne s'applique plus.
      warning: phase === 'done' || phase === 'error' ? null : state.warning,
    })),
  setError: (message) => set({ phase: 'error', error: message, warning: null }),
  setWarning: (message) => set({ warning: message }),
  setSignalingStatus: (signalingStatus) => set({ signalingStatus }),
  setLatency: (latencyMs) => set({ latencyMs }),
  setPeer: (peer) => set({ peer }),
  setConnection: (connection) => set({ connection }),
  setRole: (role) => set({ role }),
  setShareUrl: (shareUrl) => set({ shareUrl }),
  setIncoming: (incoming) => set({ incoming }),
  setProgress: (progress) => set({ progress }),
  setResult: (result) => set({ result }),
  reset: () => set({ ...INITIAL }),
}));

/**
 * Clé de message décrivant le chemin réseau retenu.
 *
 * Renvoie une **clé** et non une phrase : l'interface la traduit au moment de l'affichage,
 * ce qui la fait suivre un changement de langue.
 *
 * Dire explicitement par où passent les données est un choix : l'utilisateur doit pouvoir
 * savoir si son transfert est direct ou relayé. C'est exactement l'information que les
 * services de ce type omettent habituellement.
 */
export function describeConnectionKey(connection: ConnectionInfo | null): MessageKey {
  if (!connection || connection.candidateType === 'unknown') return 'status.pathUnknown';

  switch (connection.candidateType) {
    case 'host':
      return 'status.directLocal';
    case 'srflx':
    case 'prflx':
      return 'status.directPeer';
    case 'relay':
      return 'status.relayed';
    default:
      return 'status.established';
  }
}
